import { IContext } from "@src/context";
import { getInitialAccessUrl } from "@src/utils/initialAccessUrl";
import {
  saveSubmissionToSessionStorage,
  generateRecordId,
} from "@src/analytics";
import { getEnumByLocale } from "@src/utils/languageHelper";
import { isValidTelephone, isValidEmail } from "@src/utils/checker";
import { isOfficeHours } from "@src/utils/officeHours";
import { getEnquirePageType } from "@src/utils/getDomParam";
import { getPageCategory } from "@src/utils/helper";
import searchDataHelper from "../SearchPopup/searchDataHelper";
import { fetchAreaJson } from "../SearchResult/propertiesFetcher/allProperties";
import messages from "../BookVisit/messages";

import "./index.css";

export function setupEnquiryForm(context: IContext) {
  const { intl } = context;

  const container = document.querySelector(".container-enquiry-form");

  if (container == null) {
    return;
  }

  const baseUrl = document.body.getAttribute("data-site-domain") || "";
  const enquiryFormApiUrl = `${baseUrl}/vhmformapi.enquiry.EnquiryFormService/SubmitEnquiry`;
  const corporateFormApiUrl = `${baseUrl}/vhmformapi.enquiry.EnquiryFormService/SubmitCorporateEnquiry`;
  const pageCategory = getPageCategory();

  // update office hours hint message
  const isOfficeHoursNow = isOfficeHours(context?.locale);
  if (isOfficeHoursNow) {
    container.querySelector("[data-message='offline']")?.remove();
  } else {
    container.querySelector("[data-message='online']")?.remove();
  }

  // get property data
  let propertyId = "";
  let roomId = "";

  let submited = false;

  document.addEventListener("click", (event) => {
    if (event.target instanceof HTMLElement) {
      const button = event.target.closest(
        '[data-popup-id="enquiry-form"][data-popup-event="popup-open"]'
      );
      if (button) {
        if (
          button.hasAttribute("data-property-name") &&
          button.hasAttribute("data-property-address")
        ) {
          // set title and address
          (
            container.querySelector(
              ".container-enquiry-form-info h6"
            ) as HTMLElement
          ).innerHTML = button.getAttribute("data-property-name") || "";
          (
            container.querySelector(
              ".container-enquiry-form-info .element-address span"
            ) as HTMLElement
          ).innerHTML = button.getAttribute("data-property-address") || "";

          // set form data
          propertyId = button.getAttribute("data-property-id") || "";
          roomId = button.getAttribute("data-room-id") || "";

          (
            container.querySelector(
              ".container-enquiry-form-info"
            ) as HTMLElement
          ).style.display = "block";
        } else {
          (
            container.querySelector(
              ".container-enquiry-form-info"
            ) as HTMLElement
          ).style.display = "none";
          // clear attributes
          propertyId = "";
          roomId = "";
        }

        // set room info
        const roomInfo = container.querySelector(
          ".container-enquiry-form-info-room"
        ) as HTMLElement;
        if (roomInfo) {
          if (button.hasAttribute("data-room-rent")) {
            (
              roomInfo.querySelector('[data-name="rent"] h5') as HTMLElement
            ).innerHTML = button.getAttribute("data-room-rent") || "";
          }
          if (button.hasAttribute("data-room-size")) {
            (
              roomInfo.querySelector('[data-name="size"] h5') as HTMLElement
            ).innerHTML = button.getAttribute("data-room-size") || "";
          }
          if (button.hasAttribute("data-room-number")) {
            (
              roomInfo.querySelector('[data-name="number"] h3') as HTMLElement
            ).innerHTML = button.getAttribute("data-room-number") || "";
          }
        }

        if (
          button.hasAttribute("data-room-rent") ||
          button.hasAttribute("data-room-size") ||
          button.hasAttribute("data-room-number")
        ) {
          roomInfo.style.display = "flex";
        } else {
          roomInfo.style.display = "none";
        }
      }
    }
  });

  // toggle policy checkbox
  container
    .querySelector(".container-enquiry-form-checkbox")
    ?.addEventListener("change", (e) => {
      const checkbox = e.target as HTMLInputElement;
      const submit = container.querySelector(
        ".container-enquiry-form-submit"
      ) as HTMLButtonElement;
      submit.disabled = !checkbox.checked;
    });

  // add reCAPTCHA script
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.google.com/recaptcha/api.js?render=${
    import.meta.env.VITE_GOOGLE_RECAPTCHA_KEY
  }`;
  document.body.appendChild(script);

  const formElement = container.querySelector("form") as HTMLFormElement;

  function setSubmitError(show: boolean) {
    const removeError = () => {
      container
        ?.querySelector(".container-enquiry-form-error[data-submited]")
        ?.remove();
    };
    if (show) {
      removeError();
      container
        ?.querySelector(".container-enquiry-form-submit")
        ?.insertAdjacentHTML(
          "afterend",
          `<div class="container-enquiry-form-error" data-submited>${intl.formatMessage(
            messages.submitError
          )}</div>`
        );
    } else {
      removeError();
    }
  }

  // corporate form
  const corporate = document.querySelector(".freestyle-corporate-form");
  const cityList = corporate?.querySelector(
    ".container-enquiry-form-city-list"
  ) as HTMLElement;

  if (!corporate) {
    // submit enquiry form
    formElement?.addEventListener("submit", (e) => {
      e.preventDefault();

      setSubmitError(false);

      const formData = new FormData(formElement);

      const nameField = formElement.querySelector(
        'input[name="name"]'
      ) as HTMLInputElement;
      const emailField = formElement.querySelector(
        'input[name="email"]'
      ) as HTMLInputElement;
      const telephoneField = formElement.querySelector(
        'input[name="telephone"]'
      ) as HTMLInputElement;

      if (nameField.value === "") {
        nameField.parentElement?.setAttribute("data-invalid", "");
      } else {
        nameField.parentElement?.removeAttribute("data-invalid");
      }

      if (!isValidEmail(emailField.value)) {
        emailField.parentElement?.setAttribute("data-invalid", "");
      } else {
        emailField.parentElement?.removeAttribute("data-invalid");
      }

      if (telephoneField.value === "") {
        telephoneField.parentElement?.setAttribute("data-invalid", "empty");
      } else if (!isValidTelephone(telephoneField.value)) {
        telephoneField.parentElement?.setAttribute("data-invalid", "invalid");
      } else {
        telephoneField.parentElement?.removeAttribute("data-invalid");
      }

      if (
        !nameField.parentElement?.hasAttribute("data-invalid") &&
        !emailField.parentElement?.hasAttribute("data-invalid") &&
        !telephoneField.parentElement?.hasAttribute("data-invalid") &&
        !submited
      ) {
        const recordId = generateRecordId();
        submited = true;
        // add reCAPTCHA token
        grecaptcha.ready(function () {
          grecaptcha
            .execute(import.meta.env.VITE_GOOGLE_RECAPTCHA_KEY, {
              action: "submit",
            })
            .then(function (token) {
              window
                .fetch(enquiryFormApiUrl, {
                  credentials: "include",
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    "G-Recaptcha-Response": token,
                  },
                  body: JSON.stringify({
                    ...Object.fromEntries(formData),
                    lang: getEnumByLocale(context.locale),
                    ...(propertyId ? { property_id: Number(propertyId) } : {}),
                    ...(roomId ? { room_id: roomId } : {}),
                    current_url: window.location.href,
                    initial_access_url: getInitialAccessUrl(),
                    page_type: getEnquirePageType(),
                    record_id: recordId,
                  }),
                })
                .then((response) => {
                  if (response.status != 200) {
                    throw new Error(
                      `Failed to call filter properties, status: ${response.status} on url ${enquiryFormApiUrl}`
                    );
                  }
                  return response.json();
                })
                .then((response) => {
                  // redirect to thank you page
                  if (response.redirect_url) {
                    // GTM push `new_enquiry_form_submission` or `parking_enquiry_form_submission` event when redirected
                    saveSubmissionToSessionStorage({
                      eventName:
                        pageCategory === "parking"
                          ? "parking_enquiry_form_submission"
                          : "new_enquiry_form_submission",
                      propertyId,
                      email: emailField.value,
                      phone: telephoneField.value,
                      name: nameField.value,
                      recordId,
                    });

                    window.location.href = response.redirect_url;
                  }
                })
                .catch((error) => {
                  setSubmitError(true);
                  throw error;
                })
                .finally(() => {
                  submited = false;
                });
            });
        });
      }
    });
  } else {
    // append area city to corporate
    fetchAreaJson(context).then((areaJson) => {
      const data = searchDataHelper.pureConvertAreaResp(areaJson);
      cityList.innerHTML = [[1, 2], [3], [5, 4], [6], [7], [8, 9], [10]]
        .map((regions) => {
          return `<div class="container-enquiry-form-city-item">
        <div class="container-enquiry-form-city-item-title">
          <span>${regions
            .map((region) => data.rIdRRegionMap[region].name)
            .join("・")}</span>
        </div>
        <div class="container-enquiry-form-city-item-select">
        ${regions
          .map((region) => {
            return data.rIdPrefectureMap[region]
              .map((prefecture) => {
                if (prefecture.id) {
                  return data.pIdCityMap[prefecture.id]
                    .filter((city) => city.oArea || city.pCid !== "")
                    .map((city) => {
                      return `<label>
                <input type="checkbox" value="${city.id}" />
                <span>${city.name}</span>
              </label>`;
                    })
                    .join("");
                }
              })
              .join("");
          })
          .join("")}
        </div>
      </div>`;
        })
        .join("");

      // active first city dropdown
      // corporate
      //   .querySelector(".container-enquiry-form-city-item")
      //   ?.setAttribute("data-active", "");
    });

    // submit corporate form
    formElement?.addEventListener("submit", (e) => {
      e.preventDefault();

      setSubmitError(false);

      const formData = new FormData(formElement);

      const corporateNameField = formElement.querySelector(
        'input[name="corporate_name"]'
      ) as HTMLInputElement;
      const representativeField = formElement.querySelector(
        'input[name="representative"]'
      ) as HTMLInputElement;
      const emailField = formElement.querySelector(
        'input[name="email"]'
      ) as HTMLInputElement;
      const telephoneField = formElement.querySelector(
        'input[name="telephone"]'
      ) as HTMLInputElement;
      const residentCountField = formElement.querySelector(
        'input[name="resident_count"]'
      ) as HTMLInputElement;
      const perCapitaBudgetField = formElement.querySelector(
        'input[name="per_capita_budget"]'
      ) as HTMLInputElement;

      if (corporateNameField.value === "") {
        corporateNameField.parentElement?.setAttribute("data-invalid", "");
      } else {
        corporateNameField.parentElement?.removeAttribute("data-invalid");
      }

      if (representativeField.value === "") {
        representativeField.parentElement?.setAttribute("data-invalid", "");
      } else {
        representativeField.parentElement?.removeAttribute("data-invalid");
      }

      if (!isValidEmail(emailField.value)) {
        emailField.parentElement?.setAttribute("data-invalid", "");
      } else {
        emailField.parentElement?.removeAttribute("data-invalid");
      }

      if (telephoneField.value === "") {
        telephoneField.parentElement?.setAttribute("data-invalid", "empty");
      } else if (!isValidTelephone(telephoneField.value)) {
        telephoneField.parentElement?.setAttribute("data-invalid", "invalid");
      } else {
        telephoneField.parentElement?.removeAttribute("data-invalid");
      }

      if (isNaN(Number(residentCountField.value))) {
        residentCountField.parentElement?.setAttribute("data-invalid", "");
      } else {
        residentCountField.parentElement?.removeAttribute("data-invalid");
      }

      const checkedCities = Array.from(
        cityList.querySelectorAll('input[type="checkbox"]:checked')
      ).map((checkbox) => (checkbox as HTMLInputElement | null)?.value);

      const corporateCity = corporate?.querySelector(
        ".container-enquiry-form-city"
      );

      if (checkedCities.length < 1) {
        corporateCity?.setAttribute("data-invalid", "");
      } else {
        corporateCity?.removeAttribute("data-invalid");
      }

      if (
        !corporateCity?.hasAttribute("data-invalid") &&
        !corporateNameField.parentElement?.hasAttribute("data-invalid") &&
        !representativeField.parentElement?.hasAttribute("data-invalid") &&
        !emailField.parentElement?.hasAttribute("data-invalid") &&
        !telephoneField.parentElement?.hasAttribute("data-invalid") &&
        !residentCountField.parentElement?.hasAttribute("data-invalid") &&
        !submited
      ) {
        const recordId = generateRecordId();
        submited = true;

        // remove empty value for optional fields
        if (residentCountField.value === "") {
          formData.delete("resident_count");
        }
        if (perCapitaBudgetField.value === "") {
          formData.delete("per_capita_budget");
        }

        // add reCAPTCHA token
        grecaptcha.ready(function () {
          grecaptcha
            .execute(import.meta.env.VITE_GOOGLE_RECAPTCHA_KEY, {
              action: "submit",
            })
            .then(function (token) {
              window
                .fetch(corporateFormApiUrl, {
                  credentials: "include",
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    "G-Recaptcha-Response": token,
                  },
                  body: JSON.stringify({
                    ...Object.fromEntries(formData),
                    city_ids: checkedCities,
                    lang: getEnumByLocale(context.locale),
                    current_url: window.location.href,
                    initial_access_url: getInitialAccessUrl(),
                    page_type: getEnquirePageType(),
                    record_id: recordId,
                  }),
                })
                .then((response) => {
                  if (response.status != 200) {
                    throw new Error(
                      `Failed to call filter properties, status: ${response.status} on url ${corporateFormApiUrl}`
                    );
                  }
                  return response.json();
                })
                .then((response) => {
                  // redirect to thank you page
                  if (response.redirect_url) {
                    // GTM push `new_enquiry_form_submission` event when redirected
                    saveSubmissionToSessionStorage({
                      eventName: "new_enquiry_form_submission",
                      name: corporateNameField.value,
                      email: emailField.value,
                      phone: telephoneField.value,
                      recordId,
                      selectedCity: checkedCities as [string],
                      numberOfResidents: residentCountField.value,
                      budgetPerPerson: perCapitaBudgetField.value,
                    });

                    window.location.href = response.redirect_url;
                  }
                })
                .catch((error) => {
                  setSubmitError(true);
                  throw error;
                })
                .finally(() => {
                  submited = false;
                });
            });
        });
      }
    });
  }
}
